import { useState, useMemo } from 'react';
import { formatBidNumber } from '../utils/bidNumber';
import { parseWallClock } from '../utils/datetime';
import '../styles/calendar.css';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// Ordered for sensible legend + side-panel grouping. Labels match the wording on the
// project detail timeline. Actionable = a deadline someone must hit. Informational =
// a historical milestone already recorded.
const DEADLINE_TYPES = {
  ITBReceived:      { label: 'ITB Received',           color: '#64748b', short: 'ITB',  category: 'informational' },
  BidWalkDate:      { label: 'Bid Walk',               color: '#8b5cf6', short: 'WALK', category: 'actionable' },
  DistributedAt:    { label: 'Plans Distributed',      color: '#14b8a6', short: 'DIST', category: 'informational' },
  SubDueDate:       { label: 'Sub Bids Due',           color: '#0052F6', short: 'SUB',  category: 'actionable' },
  InternalDeadline: { label: 'Estimate Due to Manager',color: '#f59e0b', short: 'EST',  category: 'actionable' },
  GCOwnerDeadline:  { label: 'Bid Submission Deadline',color: '#dc2626', short: 'DUE',  category: 'actionable' },
  DateBidSubmitted: { label: 'Bid Submitted',          color: '#16a34a', short: 'SENT', category: 'informational' },
};

const VISIBLE_TYPES_KEY = 'bidboard-calendar-visible-types';

function loadVisibleTypes() {
  try {
    const raw = localStorage.getItem(VISIBLE_TYPES_KEY);
    if (raw) {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr)) return new Set(arr);
    }
  } catch (e) {}
  return new Set(Object.keys(DEADLINE_TYPES));
}

export default function Calendar({ projects, navigate }) {
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [selectedDay, setSelectedDay] = useState(null);
  const [visibleTypes, setVisibleTypes] = useState(loadVisibleTypes);

  function toggleType(key) {
    setVisibleTypes(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      try { localStorage.setItem(VISIBLE_TYPES_KEY, JSON.stringify([...next])); } catch (e) {}
      return next;
    });
  }

  const calendarData = useMemo(() => {
    const firstDay = new Date(currentYear, currentMonth, 1).getDay();
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const daysInPrevMonth = new Date(currentYear, currentMonth, 0).getDate();

    // Build event map
    const events = {};
    projects.forEach(p => {
      if (p.StatusName === 'Awarded' || p.StatusName === 'Not Awarded') return;
      Object.entries(DEADLINE_TYPES).forEach(([field, meta]) => {
        if (!visibleTypes.has(field)) return;
        if (p[field]) {
          // DistributedAt is a server-generated UTC timestamp; everything else is wall-clock.
          const d = field === 'DistributedAt' ? new Date(p[field]) : parseWallClock(p[field]);
          if (d.getMonth() === currentMonth && d.getFullYear() === currentYear) {
            const key = d.getDate();
            if (!events[key]) events[key] = [];
            events[key].push({
              project: p,
              type: field,
              meta,
              date: d,
            });
          }
        }
      });
    });

    // Build calendar grid
    const weeks = [];
    let week = [];
    // Previous month padding
    for (let i = firstDay - 1; i >= 0; i--) {
      week.push({ day: daysInPrevMonth - i, current: false, events: [] });
    }
    // Current month
    for (let d = 1; d <= daysInMonth; d++) {
      week.push({ day: d, current: true, events: events[d] || [], isToday: d === today.getDate() && currentMonth === today.getMonth() && currentYear === today.getFullYear() });
      if (week.length === 7) {
        weeks.push(week);
        week = [];
      }
    }
    // Next month padding
    if (week.length > 0) {
      let nextDay = 1;
      while (week.length < 7) {
        week.push({ day: nextDay++, current: false, events: [] });
      }
      weeks.push(week);
    }

    return { weeks, events };
  }, [projects, currentMonth, currentYear, visibleTypes]); // eslint-disable-line react-hooks/exhaustive-deps

  function prevMonth() {
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(y => y - 1); }
    else setCurrentMonth(m => m - 1);
    setSelectedDay(null);
  }

  function nextMonth() {
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(y => y + 1); }
    else setCurrentMonth(m => m + 1);
    setSelectedDay(null);
  }

  const selectedEvents = selectedDay ? (calendarData.events[selectedDay] || []) : [];

  return (
    <div className="calendar-container">
      <div className="calendar-main">
        <div className="calendar-header">
          <button className="btn btn-secondary btn-sm" onClick={prevMonth}>&larr;</button>
          <h3>{MONTHS[currentMonth]} {currentYear}</h3>
          <button className="btn btn-secondary btn-sm" onClick={nextMonth}>&rarr;</button>
        </div>

        <div className="calendar-legend">
          {Object.entries(DEADLINE_TYPES).map(([key, meta]) => {
            const visible = visibleTypes.has(key);
            return (
              <button
                key={key}
                type="button"
                className={`legend-item legend-${meta.category} ${visible ? '' : 'legend-item-off'}`}
                onClick={() => toggleType(key)}
                title={visible ? `Click to hide ${meta.label}` : `Click to show ${meta.label}`}
              >
                <span className={`legend-dot legend-dot-${meta.category}`} style={{ backgroundColor: meta.color }} />
                {meta.label}
              </button>
            );
          })}
        </div>

        <div className="calendar-grid">
          {DAYS.map(d => <div key={d} className="calendar-day-header">{d}</div>)}
          {calendarData.weeks.flat().map((cell, i) => (
            <div
              key={i}
              className={`calendar-cell ${cell.current ? '' : 'other-month'} ${cell.isToday ? 'today' : ''} ${cell.events.length > 0 ? 'has-events' : ''} ${selectedDay === cell.day && cell.current ? 'selected' : ''}`}
              onClick={() => cell.current && cell.events.length > 0 && setSelectedDay(cell.day)}
            >
              <span className="cell-day">{cell.day}</span>
              <div className="cell-events">
                {cell.events.slice(0, 3).map((ev, j) => (
                  <div key={j} className={`cell-event-dot cell-event-${ev.meta.category}`} style={{ backgroundColor: ev.meta.color }} title={`${ev.meta.label}: ${ev.project.ProjectName}`}>
                    <span className="cell-event-label">{ev.meta.short}</span>
                    <span className="cell-event-project"> - {ev.project.ProjectName}</span>
                  </div>
                ))}
                {cell.events.length > 3 && <span className="cell-more">+{cell.events.length - 3}</span>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Side panel for selected day */}
      <div className="calendar-detail">
        {selectedDay ? (
          <>
            <h4>{MONTHS[currentMonth]} {selectedDay}, {currentYear}</h4>
            <div className="detail-events">
              {selectedEvents.sort((a, b) => a.date - b.date).map((ev, i) => (
                <div key={i} className="detail-event" onClick={() => navigate(`/projects/${ev.project.ProjectId}`)}>
                  <div className="detail-event-type" style={{ borderLeftColor: ev.meta.color }}>
                    <span className="detail-type-label">{ev.meta.label}</span>
                    <span className="detail-time">
                      {ev.date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                    </span>
                  </div>
                  <div className="detail-event-info">
                    <div className="detail-project-name">{ev.project.ProjectName}</div>
                    <div className="detail-project-meta">
                      {formatBidNumber(ev.project.BidNumber, ev.project.RevisionNumber)} &middot; {ev.project.Estimator || 'Unassigned'} &middot; {ev.project.StatusName}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="detail-empty">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            <p>Select a day to see deadlines</p>
          </div>
        )}
      </div>
    </div>
  );
}
